<script lang="ts" context="module">
  /**
   * Props Convenience Type Export
   * ---------------------------------------------------------------------------
   */

  import type { ComponentProps } from "svelte";
  import Card from "./Card.svelte"; // self reference
  export type CardProps = ComponentProps<Card>;
</script>

<script lang="ts">
  /**
   * Imports
   * ---------------------------------------------------------------------------
   */

  import Button, { type ButtonProps } from "@elements/Button.svelte";
  import { trimString } from "@utils/string";
  import { twJoin, twMerge } from "tailwind-merge";
  import Color from "color";
  import CardImage, {
    type CardImageAspectRatio,
  } from "./Card/CardImage.svelte";
  import CardTag from "./Card/CardTag.svelte";
  import CardAvatar, { type CardAvatarProps } from "./Card/CardAvatar.svelte";
  import CardTags from "./Card/CardTags.svelte";
  import PlayButton from "./Card/PlayButton.svelte";
  import ArticleCard from "./Card/ArticleCard.svelte";
  /**
   * Props
   * ---------------------------------------------------------------------------
   */

  /** variant: determines the layout and style of the Card. */
  export let variant:
    | "default"
    | "podcast-episode"
    | "blog-article"
    | "book"
    | "video-tile" = "default";

  /** orientation to use for Card layout */
  export let orientation: "portrait" | "landscape" = "portrait";

  /** text tag for single card type text bubble (displayed upper left) */
  export let tag: string = "";

  /** multiple text tags */
  export let tags: string[] = [];

  /**
   * background image URL;
   * if set, then cover the card background with this image
   */
  export let backgroundImageSrc: string | undefined = undefined;

  /**
   * background image gradient
   * if true, then overlay a darkening gradient on top of the background image
   */
  export let backgroundImageGradient: boolean = true;

  /**
   * background accent color, in form "#000000";
   * if set, then generate a conic-gradient background
   * NOTE: use either background image or background accent color, not both
   */
  export let backgroundAccentColor: string | undefined = undefined;

  /** featured image URL */
  export let featuredImageSrc: string | undefined = undefined;

  /** featured image aspect ratio */
  export let featuredImageAspectRatio: CardImageAspectRatio = "landscape";

  /** avatar */
  export let avatar: CardAvatarProps | undefined = undefined;

  /** leader text, usually the topmost text above the title */
  export let leader: string = "";

  /** main title text */
  export let title: string = "";

  /** subtitle text */
  export let subtitle: string = "";

  /** description text */
  export let description: string = "";

  /** if true, then trim the description text to `trimDescriptionLength` chars */
  export let trimDescriptionBool: boolean = true;

  /** the number of chars to trim the description text, if `trimDescriptionBool` is true */
  export let trimDescriptionLength: number = 125;

  /** details text, very small and usually below the description text */
  export let details: string = "";

  /** Left Call to Action */
  export let leftCTA: ButtonProps | undefined | string = undefined;

  /** Right Call to Action */
  export let rightCTA: ButtonProps | undefined | string = undefined;

  /** href - if set, then the entire Card is a link to this URL */
  export let href: undefined | string = undefined;

  /** if true, then this Card is highlighted as being focused */
  export let isFocused: boolean = false;

  /**
   * If disabled, then this Card (including its CTA buttons) cannot
   * be interacted with and does not receive pointer events.
   */
  export let isDisabled: boolean = false;

  /**
   * Callback for the Play button click event.
   */
  export let onPlayButtonClick: VoidFunction | null = null;

  /** pass any custom class to the topmost element */
  export let { class: classProp = "" } = $$props;

  /**
   * Data / State
   * ---------------------------------------------------------------------------
   */

  let isZoomed = false;
  const isLandscape = orientation === "landscape";
  const isPortrait = orientation === "portrait";

  const finalDescription = trimDescriptionBool
    ? trimString(description, trimDescriptionLength)
    : description;

  const isLeftCtaAString = typeof leftCTA === "string";
  const isRightCtaAString = typeof rightCTA === "string";
  const isLeftCtaAnObjectWithLink =
    typeof leftCTA === "object" && !!leftCTA?.href;
  const isRightCtaAnObjectWithLink =
    typeof rightCTA === "object" && !!rightCTA?.href;
  const isAnyCtaSet =
    isLeftCtaAString ||
    isRightCtaAString ||
    isLeftCtaAnObjectWithLink ||
    isRightCtaAnObjectWithLink;

  /**
   * Functions
   * ---------------------------------------------------------------------------
   */

  function zoomIn(e: any) {
    isZoomed = true;
  }

  function zoomOut(e: any) {
    isZoomed = false;
  }
</script>

<!-- Template ----------------------------------------------------------------->

<a
  on:mouseleave={zoomOut}
  on:mouseenter={zoomIn}
  {href}
  class={twJoin(
    "@container overflow-hidden bg-n900",
    "flex flex-col",
    "align-start relative m-0",
    "transition-transform duration-300", // for zoom transition
    "rounded-2xl", // standard
    "md:rounded-3xl", // medium
    isFocused && "border-p100 border-solid border",
    isDisabled && "cursor-auto pointer-events-none",
    isZoomed ? "md:scale-105" : "scale-100",
    classProp,
  )}
>
  <!-- Background gradient (based on one accent color) -->
  {#if !!backgroundAccentColor}
    <div
      class="absolute inset-0 blur-md scale-110"
      style={"background: " +
        "conic-gradient(" +
        "from -29deg at 47.37% 66.44%, " +
        `${Color(backgroundAccentColor).darken(0.65).hex()} 82.11655855178833deg, ` +
        `${Color(backgroundAccentColor).darken(0.35).hex()} 151.86856269836426deg, ` +
        `${Color(backgroundAccentColor).darken(0.05).hex()} 182.34807014465332deg, ` +
        `${backgroundAccentColor} 240.00000715255737deg, ` +
        `${Color(backgroundAccentColor).darken(0.65).hex()} 360deg);`}
    />
  {/if}

  <!-- Background image -->
  {#if !!backgroundImageSrc}
    <div class="absolute inset-0">
      <img
        loading="lazy"
        class="object-cover w-full h-full"
        src={backgroundImageSrc}
        alt=""
        style={backgroundImageGradient
          ? "mask-image: linear-gradient(270deg, black, transparent);"
          : undefined}
      />
    </div>
  {/if}

  <!-- Tag -->
  {#if tag}
    <CardTag
      text={tag}
      class={twJoin("absolute z-30 top-5", isPortrait ? "left-5" : "right-5")}
      variant={isPortrait ? "darkGhost" : "light"}
    />
  {/if}

  {#if variant === "default"}
    <!-- Portrait (only): Default Variant -->

    <!-- Image -->
    <CardImage
      imageSrc={featuredImageSrc}
      imageAspectRatio={featuredImageAspectRatio}
      imageAspectRatioMobile={featuredImageAspectRatio}
      altText={title}
      {isDisabled}
    />

    <!-- Text -->
    {#if leader || title || finalDescription || details}
      <div
        class={twMerge(
          "p-8 pb-6 row-span-5",
          isAnyCtaSet ? "mb-12" : "mb-0",
          isDisabled && "opacity-50",
        )}
      >
        {#if leader}
          <h3 class="text-s100 text-body-small font-light pb-2">
            {leader}
          </h3>
        {/if}
        {#if title}
          <h2 class="text-white text-h5 pb-2 font-bold">{title}</h2>
        {/if}
        {#if finalDescription}
          <p class="text-white text-body-base font-extralight pb-2">
            {@html finalDescription}
          </p>
        {/if}
        {#if details}
          <p class="text-s100 text-body-extra-small pb-2">{details}</p>
        {/if}
      </div>
    {/if}

    <!-- CTA buttons -->
    {#if isAnyCtaSet}
      <div
        class="bottom-0 z-50 left-0 p-4 w-full flex justify-between absolute"
      >
        {#if isLeftCtaAString}
          <p class="text-white px-3 py-4 text-body-small">
            {leftCTA}
          </p>
        {/if}
        <!-- redundant "object" typecheck here to make Svelte/TS happy -->
        {#if isLeftCtaAnObjectWithLink && typeof leftCTA === "object"}
          <Button
            {...{
              ...leftCTA,
              size: "small",
              variant: "cta",
              color: "white",
            }}
          />
        {/if}
        {#if isRightCtaAString}
          <p class="text-white px-3 py-4 ml-auto text-body-small">
            {rightCTA}
          </p>
        {/if}
        <!-- redundant "object" typecheck here to make Svelte/TS happy -->
        {#if isRightCtaAnObjectWithLink && typeof rightCTA === "object"}
          <Button
            {...{
              ...rightCTA,
              class: "ml-auto",
              size: "small",
              variant: "cta",
              color: "white",
            }}
          />
        {/if}
      </div>
    {/if}
  {:else if variant === "podcast-episode" || variant === "video-tile"}
    {#if isLandscape}
      <!-- Landscape: Podcast Episode, Video Tile -->
      <div class={"h-full relative"}>
        <!-- Image -->
        <CardImage
          imageSrc={featuredImageSrc}
          imageAspectRatio={featuredImageAspectRatio}
          imageAspectRatioMobile={"square"}
          altText={title}
          {isDisabled}
          class={twJoin(
            "float-left",
            "p-4 max-w-24", // mobile
            "sm:max-w-[200px]", // small
            "md:p-8 md:max-w-[350px]", // medium
          )}
          imageClass={"rounded-md"}
        />
        <div class={twJoin("z-10 h-full p-4 md:p-8")}>
          <!-- title, subtitle, description -->
          <h3 class="text-white">{title}</h3>
          <h6 class="text-s100 mt-2">{subtitle}</h6>
          <p class="wah-body-small text-white mt-4">{finalDescription}</p>
          <!-- play button, tags -->
          <div class={"mt-6 flex flex-row items-center gap-2"}>
            <PlayButton onClick={onPlayButtonClick} />
            <CardTags {tags} />
          </div>
        </div>
      </div>
    {:else}
      <!-- Portrait: Podcast Episode, Video Tile -->
      <div class={"h-full relative flex flex-col"}>
        <!-- Image -->
        <CardImage
          imageSrc={featuredImageSrc}
          imageAspectRatio={featuredImageAspectRatio}
          imageAspectRatioMobile={featuredImageAspectRatio}
          altText={title}
        />
        <div class={"z-10 h-full p-8"}>
          <!-- title, subtitle, description -->
          <h3 class="text-white">{title}</h3>
          <h6 class="text-s100 mt-2">{subtitle}</h6>
          <p class="wah-body-small text-white mt-4">{finalDescription}</p>

          <!-- play button, tags -->
          <div class="mt-4 flex flex-row items-center justify-between gap-2">
            <PlayButton onClick={onPlayButtonClick} />
            <CardTags {tags} />
          </div>
        </div>
      </div>
    {/if}
  {:else if variant === "blog-article"}
    {#if isLandscape}
      <!-- Landscape: Blog Article -->
      <div class={"h-full relative p-8"}>
        <div class="grid grid-cols-[1fr_auto] gap-x-8">
          <div>
            <!-- leader, title -->
            <h6 class="text-white opacity-50">{leader}</h6>
            <p class="text-white wah-hero-2">{title}</p>
          </div>
          <div class="row-span-1 md:row-span-3">
            <!-- featured image -->
            <CardImage
              imageSrc={featuredImageSrc}
              imageAspectRatio={"square"}
              imageAspectRatioMobile={"square"}
              altText={title}
              imageClass={"rounded-md"}
              class="size-24 sm:size-32 md:size-48 lg:size-64"
            />
          </div>
          <div class="col-span-2 md:col-span-1">
            <!-- description -->
            <p class={"mt-2 wah-body-large text-white"}>
              {finalDescription}
            </p>
          </div>
          <div class="col-span-2 lg:col-span-1">
            <!-- avatar, tags -->
            <div class="mt-3 flex flex-row items-center justify-between gap-2">
              <CardAvatar {...avatar} />
              <CardTags {tags} />
            </div>
          </div>
        </div>
      </div>
    {:else}
      <ArticleCard
        {leader}
        {title}
        {description}
        {tags}
        {avatar}
        {featuredImageSrc}
      />
    {/if}
  {/if}
</a>
